<template>
  <div
    class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]"
  >
    <div class="w-full text-[#414141] font-sans">
      <h2 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">
        Our Story
      </h2>

      <div v-if="isLoading" class="text-gray-600 font-sans py-4">
        <p>Loading data...</p>
      </div>
      <div
        v-else-if="!authStore.user?.organization_id"
        class="p-4 bg-yellow-50 font-sans border border-yellow-200 rounded-md mb-4"
      >
        <p class="text-amber-700">Unable to load organization data. Please try refreshing the page.</p>
        <button
          @click="authStore.fetchUserData"
          class="bg-amber-600 font-sans text-white px-4 py-1 rounded-md mt-2"
        >
          Retry Loading Organization Data
        </button>
      </div>
      <div v-else>
        <div class="w-full">
          <QuestionField
            v-model="answerState.userAnswer"
            :question="questionText"
            placeholder="Enter your organization's story..."
            :error-message="errorMessage || (v$.userAnswer.$error ? v$.userAnswer.$errors[0]?.$message : '')"
          />
        </div>
        
        <!-- Word counter - positioned on the left side of the bottom of the input area -->
        <div class="flex gap-4 mt-2">
          <div class="question-header" style="flex: 3;"></div>
          <div class="input-wrapper" style="flex: 7;">
            <div class="text-sm text-gray-600 font-sans text-left">
              <span :class="{ 'text-red-500': currentWordCount > 294 }">
                {{ currentWordCount }}/294 words
              </span>
              <span v-if="currentWordCount > 294" class="text-red-500 ml-2">
                (Word limit exceeded)
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed } from "vue";
import QuestionField from "@/components/QuestionField.vue";
import { useAuthStore } from "@/stores/auth";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import global from "@/global";
import { useStepValidation } from "@/composables/useStepValidation";
const isLoading = ref(true);
const questionText = ref("");
const errorMessage = ref("");
const answerState = reactive({ userAnswer: "" });
const lastSavedValue = ref("");

// Add computed property for word count
const currentWordCount = computed(() => {
  if (!answerState.userAnswer) return 0;
  return answerState.userAnswer.trim().split(/\s+/).filter(word => word.length > 0).length;
});

// Define categoryId and questionId as refs
const categoryId = ref("");
const questionId = ref("");

// Validation rules
const rules = {
  userAnswer: {
    required: helpers.withMessage("Answer is required!", required),
    maxWords: helpers.withMessage("You cannot enter more than 294 words!", (value) => {
      if (!value) return true;
      const wordCount = value.trim().split(/\s+/).filter(word => word.length > 0).length;
      return wordCount <= 294;
    }),
  },
};
const v$ = useVuelidate(rules, answerState);

// Fetching the auth store
const authStore = useAuthStore();

// Custom validation function for the useStepValidation composable
const validateStep = async () => {
  await v$.value.$validate();

  if (v$.value.$invalid) {
    return false;
  }

  if (answerState.userAnswer !== lastSavedValue.value) {
    try {
      await saveUserAnswer();
      return true;
    } catch (error) {
      console.error("Failed to save answer:", error);
      errorMessage.value =
        "Please ensure your answer is saved before proceeding.";
      return false;
    }
  }

  return !v$.value.$invalid;
};

// Use the step validation composable
useStepValidation({
  initialValidState: false,
  validateFn: validateStep,
});

// Fetching question data directly using fetch API
async function fetchQuestionData() {
  try {
    const res = await fetch(`${global.API_URL}/questions/finalize_case_for_support_story`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const data = await res.json();
    
    if (!data || !data.question) {
      throw new Error('Invalid response structure from API');
    }

    questionText.value = data.question.title || '';
    categoryId.value = data.question.category_id || '';
    questionId.value = data.question.id || '';

    if (!questionId.value) {
      throw new Error('Question ID is missing from the response');
    }
  } catch (error) {
    console.error('Error fetching question data:', error);
    errorMessage.value = "Failed to load question data. Please try again.";
    isLoading.value = false;
  }
}

// Fetching user answer directly using fetch API
async function getUserAnswer() {
  const organizationId = authStore.user.organization_id;

  if (!organizationId) {
    console.error("Invalid organizationId:", organizationId);
    errorMessage.value = "Invalid organization ID. Please try refreshing the page.";
    return;
  }

  if (!questionId.value) {
    console.error("Invalid questionId:", questionId.value);
    errorMessage.value =
      "Question ID not available. Please try refreshing the page.";
    return;
  }

  try {
    const res = await fetch(
      `${global.API_URL}/answers?question_id=${questionId.value}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
        },
      }
    );

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const data = await res.json();
    let answer = data.answer;
    if (answer != null) {
      answerState.userAnswer = answer.answer;
      lastSavedValue.value = answer.answer;
    }
  } catch (error) {
    console.error("Failed to fetch user answer:", error);
    errorMessage.value =
      "Failed to load your previous answer. You can still create a new one.";
  }
}

// Saving user answer directly using fetch API
async function saveUserAnswer() {
  const isValid = await v$.value.$validate();
  if (!isValid) {
    return false;
  }

  if (answerState.userAnswer === lastSavedValue.value) {
    return true;
  }

  const organizationId = authStore.user.organization_id;

  if (!organizationId) {
    errorMessage.value = "Invalid organization ID. Please try refreshing the page.";
    return false;
  }

  try {
    const res = await fetch(`${global.API_URL}/answers`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        category_id: categoryId.value,
        question_id: questionId.value,
        answer: answerState.userAnswer,
      }),
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    errorMessage.value = "";
    lastSavedValue.value = answerState.userAnswer;
    return true;
  } catch (error) {
    errorMessage.value = "Failed to save your answer. Please try again.";
    console.error("Failed to save user answer:", error);
    return false;
  }
}

onMounted(async () => {
  try {
    isLoading.value = true;
    errorMessage.value = "";

    await authStore.fetchUserData();

    if (!authStore.user?.organization_id) {
      errorMessage.value = "Unable to load organization data. Please try refreshing the page.";
      console.error("No organization ID available");
      return;
    }

    await fetchQuestionData();

    if (!questionId.value) {
      errorMessage.value = "Unable to load question data. Please try refreshing the page.";
      console.error("No question ID available");
      return;
    }

    await getUserAnswer();

  } catch (error) {
    console.error("Error during component initialization:", error);
    errorMessage.value = "An error occurred while loading the data. Please try refreshing the page.";
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped>

</style> 